import { createContextApi, invalidatesOne, providesOne, providesList, createCursorPagedEndpoint, CACHE_POLICY } from '@quickbee/store';
import { getContextClient } from '@quickbee/api-client';

// src/index.ts
var TENANT_TAGS = [
  "Tenant",
  "Branch",
  "StaffUser",
  "ChannelCharge",
  "FeatureFlag",
  "PublicBranding"
];
var PLATFORM_SCOPE = "PLATFORM";
var publicTenantEndpoints = (build) => ({
  getPublicBrandingByHost: build.query({
    query: (host) => ({ url: `/tenants/public/by-host/${encodeURIComponent(host)}`, publicRoute: true }),
    providesTags: (_r, _e, host) => [{ type: "PublicBranding", id: host }],
    // Branding is stable and cacheable; the backend serves it with a long
    // Cache-Control and a tenant-scoped ETag.
    keepUnusedDataFor: 900
  })
});
var tenantEndpoints = (build) => ({
  // ─── Tenants ────────────────────────────────────────────────────────────────
  /**
   * Rows are `TenantSummaryResponse`, not `TenantResponse`.
   *
   * The endpoint accepts a `cursor` and reports `hasMore`, but tenant-service's
   * repository builds its page with `Page.ofOffset(…, offset = 0, …)` and
   * therefore never returns a `nextCursor`. `nextPageArgs` consequently returns
   * `null` for page 2, so a "load more" control correctly stays disabled rather
   * than re-fetching page 1 forever. Filtering with `search`/`status` is the
   * usable way to reach a tenant beyond the first page until the backend
   * implements keyset paging.
   */
  listTenants: build.query({
    query: (params) => ({ url: "/tenants", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Tenant", result)
  }),
  getTenant: build.query({
    query: (id) => ({ url: `/tenants/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Tenant", id),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  createTenant: build.mutation({
    query: ({ draftId, body }) => ({
      url: "/tenants",
      method: "POST",
      body,
      idempotencyKey: `tenant-create:${draftId}`
    }),
    invalidatesTags: [{ type: "Tenant", id: "LIST" }]
  }),
  /** Full-replace profile update — send the whole profile, not a patch. */
  updateTenant: build.mutation({
    query: ({ id, requestId, body }) => ({
      url: `/tenants/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `tenant-update:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  /** Returns the whole tenant, not the branding fragment that was submitted. */
  updateBranding: build.mutation({
    query: ({ id, requestId, body }) => ({
      url: `/tenants/${id}/branding`,
      method: "PUT",
      body,
      idempotencyKey: `tenant-branding:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  updateSpread: build.mutation({
    query: ({ id, requestId, spreadPercent }) => ({
      url: `/tenants/${id}/spread`,
      method: "PUT",
      body: { spreadPercent },
      idempotencyKey: `tenant-spread:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  // ─── Collection-channel charges ─────────────────────────────────────────────
  listChannelCharges: build.query({
    query: (id) => ({ url: `/tenants/${id}/channel-charges` }),
    providesTags: (_r, _e, id) => [{ type: "ChannelCharge", id }],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /**
   * Upserts one channel's policy and returns the tenant's **whole** charge list.
   * Guarded by `tenants:manage-charges`, deliberately narrower than
   * `tenants:update`: this decides how much of a customer's money becomes gold.
   */
  updateChannelCharge: build.mutation({
    query: ({ id, channel, requestId, body }) => ({
      url: `/tenants/${id}/channel-charges/${channel}`,
      method: "PUT",
      body,
      idempotencyKey: `tenant-channel-charge:${id}:${channel}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [{ type: "ChannelCharge", id }]
  }),
  // ─── Feature flags ──────────────────────────────────────────────────────────
  /** Platform-scope catalogue: every entry's `tenantOverride` is `null`. */
  getPlatformFeatureFlagCatalog: build.query({
    query: () => ({ url: "/tenants/feature-flags" }),
    providesTags: [{ type: "FeatureFlag", id: PLATFORM_SCOPE }],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /** Tenant-scope catalogue: platform defaults plus this tenant's overrides. */
  getTenantFeatureFlagCatalog: build.query({
    query: (id) => ({ url: `/tenants/${id}/feature-flags` }),
    providesTags: (_r, _e, id) => [{ type: "FeatureFlag", id }],
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor
  }),
  /** `204`. Send only the keys being changed — the map must be non-empty. */
  toggleFeatureFlags: build.mutation({
    query: ({ id, requestId, changes }) => ({
      url: `/tenants/${id}/feature-flags`,
      method: "PUT",
      body: { changes },
      idempotencyKey: `tenant-feature-flags:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => [{ type: "FeatureFlag", id }]
  }),
  // ─── Tenant lifecycle ───────────────────────────────────────────────────────
  /**
   * `ONBOARDING → ACTIVE`, and rejected from any other status, so the entity id
   * is a sufficient idempotency key. Also rejected while `schemaProvisioned` is
   * `false` — check that before enabling the control.
   */
  activateTenant: build.mutation({
    query: (id) => ({
      url: `/tenants/${id}/activate`,
      method: "POST",
      idempotencyKey: `tenant-activate:${id}`
    }),
    invalidatesTags: (_r, _e, id) => invalidatesOne("Tenant", id)
  }),
  /** `ACTIVE → SUSPENDED`. Reversible, so the key carries `requestId`. */
  suspendTenant: build.mutation({
    query: ({ id, requestId, reason }) => ({
      url: `/tenants/${id}/suspend`,
      method: "POST",
      body: { reason },
      idempotencyKey: `tenant-suspend:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  /** `SUSPENDED → ACTIVE`. Reversible, so the key carries `requestId`. */
  reactivateTenant: build.mutation({
    query: ({ id, requestId }) => ({
      url: `/tenants/${id}/reactivate`,
      method: "POST",
      idempotencyKey: `tenant-reactivate:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  /**
   * `204`, terminal and irreversible (INV-16), and it takes **no body** — the
   * reason field belongs to `suspend` alone. Keyed on the id because a second
   * archive is rejected by the aggregate anyway.
   */
  archiveTenant: build.mutation({
    query: (id) => ({
      url: `/tenants/${id}/archive`,
      method: "POST",
      idempotencyKey: `tenant-archive:${id}`
    }),
    invalidatesTags: (_r, _e, id) => invalidatesOne("Tenant", id)
  }),
  /** Repeatable by definition, so the key must carry `requestId`. */
  retryProvisioning: build.mutation({
    query: ({ id, requestId }) => ({
      url: `/tenants/${id}/retry-provisioning`,
      method: "POST",
      idempotencyKey: `tenant-retry-provisioning:${id}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Tenant", id)
  }),
  // ─── Branches ───────────────────────────────────────────────────────────────
  /** A plain array — `TenantBranchesController#list` takes no `PageRequest`. */
  listBranches: build.query({
    query: (tenantId) => ({ url: `/tenants/${tenantId}/branches` }),
    providesTags: (result) => providesList("Branch", result)
  }),
  getBranch: build.query({
    query: ({ tenantId, branchId }) => ({ url: `/tenants/${tenantId}/branches/${branchId}` }),
    providesTags: (_r, _e, { branchId }) => providesOne("Branch", branchId)
  }),
  createBranch: build.mutation({
    query: ({ tenantId, draftId, body }) => ({
      url: `/tenants/${tenantId}/branches`,
      method: "POST",
      body,
      idempotencyKey: `branch-create:${tenantId}:${draftId}`
    }),
    invalidatesTags: [{ type: "Branch", id: "LIST" }]
  }),
  /** Full replace; `branchCode` is immutable and is not part of the body. */
  updateBranch: build.mutation({
    query: ({ tenantId, branchId, requestId, body }) => ({
      url: `/tenants/${tenantId}/branches/${branchId}`,
      method: "PUT",
      body,
      idempotencyKey: `branch-update:${branchId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { branchId }) => invalidatesOne("Branch", branchId)
  }),
  /**
   * INV-11: 409s with `activeStaffCount` while staff are still assigned. Confirm
   * with the operator, then re-call with `force: true` and the **same**
   * `requestId` — the first call never reached the handler, so its key was never
   * stored, and reusing it keeps the confirmed retry a single logical action.
   */
  deactivateBranch: build.mutation({
    query: ({ tenantId, branchId, requestId, force }) => ({
      url: `/tenants/${tenantId}/branches/${branchId}/deactivate`,
      method: "POST",
      params: { force },
      idempotencyKey: `branch-deactivate:${branchId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { branchId }) => invalidatesOne("Branch", branchId)
  }),
  reactivateBranch: build.mutation({
    query: ({ tenantId, branchId, requestId }) => ({
      url: `/tenants/${tenantId}/branches/${branchId}/reactivate`,
      method: "POST",
      idempotencyKey: `branch-reactivate:${branchId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { branchId }) => invalidatesOne("Branch", branchId)
  }),
  // ─── Tenant staff users ─────────────────────────────────────────────────────
  listStaffUsers: build.query({
    query: ({ tenantId, ...params }) => ({
      url: `/tenants/${tenantId}/users`,
      params
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("StaffUser", result)
  }),
  getStaffUser: build.query({
    query: ({ tenantId, userId }) => ({ url: `/tenants/${tenantId}/users/${userId}` }),
    providesTags: (_r, _e, { userId }) => providesOne("StaffUser", userId)
  }),
  /** `role` accepts the display string (`'Tenant Admin'`) or the enum constant. */
  inviteStaffUser: build.mutation({
    query: ({ tenantId, draftId, body }) => ({
      url: `/tenants/${tenantId}/users`,
      method: "POST",
      body,
      idempotencyKey: `staff-invite:${tenantId}:${draftId}`
    }),
    invalidatesTags: [{ type: "StaffUser", id: "LIST" }]
  }),
  /** Full replace; `email` is immutable and is not part of the body. */
  updateStaffUser: build.mutation({
    query: ({ tenantId, userId, requestId, body }) => ({
      url: `/tenants/${tenantId}/users/${userId}`,
      method: "PUT",
      body,
      idempotencyKey: `staff-update:${userId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("StaffUser", userId)
  }),
  /**
   * Three-state, and cyclic — `requestId` is what keeps an
   * ACTIVE → SUSPENDED → ACTIVE sequence from replaying a cached response.
   */
  changeStaffUserStatus: build.mutation({
    query: ({ tenantId, userId, requestId, status }) => ({
      url: `/tenants/${tenantId}/users/${userId}/status`,
      method: "POST",
      body: { status },
      idempotencyKey: `staff-status:${userId}:${requestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("StaffUser", userId)
  })
});
async function fetchPublicBranding(host) {
  try {
    const client = getContextClient({
      context: "tenant",
      publicClient: true,
      mocks: () => import('./mocks-NNLSMJTQ.js').then((module) => module.tenantMocks)
    });
    return await client.get(
      `/tenants/public/by-host/${encodeURIComponent(host)}`,
      { publicRoute: true }
    );
  } catch {
    return null;
  }
}

// src/index.ts
var loadMocks = () => import('./mocks-NNLSMJTQ.js').then((module) => module.tenantMocks);
var tenantApi = createContextApi({
  context: "tenant",
  reducerPath: "tenantApi",
  tagTypes: TENANT_TAGS,
  mocks: loadMocks,
  endpoints: tenantEndpoints
});
var publicTenantApi = createContextApi({
  context: "tenant",
  reducerPath: "publicTenantApi",
  tagTypes: TENANT_TAGS,
  mocks: loadMocks,
  publicClient: true,
  endpoints: publicTenantEndpoints
});
var {
  // Tenants
  useListTenantsQuery,
  useGetTenantQuery,
  useCreateTenantMutation,
  useUpdateTenantMutation,
  useUpdateBrandingMutation,
  useUpdateSpreadMutation,
  // Collection-channel charges
  useListChannelChargesQuery,
  useUpdateChannelChargeMutation,
  // Feature flags
  useGetPlatformFeatureFlagCatalogQuery,
  useGetTenantFeatureFlagCatalogQuery,
  useToggleFeatureFlagsMutation,
  // Lifecycle
  useActivateTenantMutation,
  useSuspendTenantMutation,
  useReactivateTenantMutation,
  useArchiveTenantMutation,
  useRetryProvisioningMutation,
  // Branches
  useListBranchesQuery,
  useGetBranchQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeactivateBranchMutation,
  useReactivateBranchMutation,
  // Staff users
  useListStaffUsersQuery,
  useGetStaffUserQuery,
  useInviteStaffUserMutation,
  useUpdateStaffUserMutation,
  useChangeStaffUserStatusMutation
} = tenantApi;
var { useGetPublicBrandingByHostQuery } = publicTenantApi;

export { PLATFORM_SCOPE, TENANT_TAGS, fetchPublicBranding, publicTenantApi, tenantApi, useActivateTenantMutation, useArchiveTenantMutation, useChangeStaffUserStatusMutation, useCreateBranchMutation, useCreateTenantMutation, useDeactivateBranchMutation, useGetBranchQuery, useGetPlatformFeatureFlagCatalogQuery, useGetPublicBrandingByHostQuery, useGetStaffUserQuery, useGetTenantFeatureFlagCatalogQuery, useGetTenantQuery, useInviteStaffUserMutation, useListBranchesQuery, useListChannelChargesQuery, useListStaffUsersQuery, useListTenantsQuery, useReactivateBranchMutation, useReactivateTenantMutation, useRetryProvisioningMutation, useSuspendTenantMutation, useToggleFeatureFlagsMutation, useUpdateBranchMutation, useUpdateBrandingMutation, useUpdateChannelChargeMutation, useUpdateSpreadMutation, useUpdateStaffUserMutation, useUpdateTenantMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map